import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Pricing = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const pricingExamples = [
    {
      type: 'Cafe',
      icon: FiIcons.FiCoffee,
      current: '$250/mo',
      offered: '$150/mo',
      savings: '$100/mo',
      description: 'High-speed internet for POS systems, customer WiFi, and online orders'
    },
    {
      type: 'Retail',
      icon: FiIcons.FiShoppingBag,
      current: '$350/mo',
      offered: '$210/mo',
      savings: '$140/mo',
      description: 'Reliable connection for inventory management, security cameras, and payment processing'
    },
    {
      type: 'Office',
      icon: FiIcons.FiBriefcase,
      current: '$450/mo',
      offered: '$270/mo',
      savings: '$180/mo',
      description: 'Business-grade internet for VoIP phones, cloud services, and team collaboration'
    },
    {
      type: 'Hotel',
      icon: FiIcons.FiHome,
      current: '$800/mo',
      offered: '$480/mo',
      savings: '$320/mo',
      description: 'High-capacity connection for guest WiFi, property management, and security systems'
    }
  ];

  const negotiationFactors = [
    'Bundle discounts with phone/TV services',
    'Multi-year contract incentives',
    'Competitive provider offers',
    'Business account upgrades',
    'Seasonal pricing promotions'
  ];

  return (
    <section id="pricing" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Real Savings for{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
              Your Business
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See how much Hudson Valley businesses are saving on their internet bills
          </p>
        </motion.div>

        {/* Pricing Examples */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {pricingExamples.map((example, index) => (
            <motion.div
              key={example.type}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 relative overflow-hidden"
            >
              {/* Savings Badge */}
              <div className="absolute top-4 right-4 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                Save {example.savings}
              </div>

              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <SafeIcon icon={example.icon} className="w-8 h-8 text-blue-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-4">{example.type}</h3>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 line-through text-sm">{example.current}</span>
                  <span className="text-gray-400 text-xs">Current</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-blue-600">{example.offered}</span>
                  <span className="text-green-600 text-xs font-semibold">Offered</span>
                </div>
              </div>

              <p className="text-gray-600 text-sm mb-4">{example.description}</p>

              <div className="pt-4 border-t border-gray-100">
                <div className="text-green-600 font-semibold text-sm mb-2">
                  Total Savings: {example.savings}/month
                </div>
                <div className="text-xs text-gray-500">
                  That's {parseInt(example.savings.replace('$', '').replace('/mo', '')) * 12} savings per year!
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-12 text-white mb-16"
        >
          <h3 className="text-3xl font-bold mb-4">
            Want Your Exact Savings Quote?
          </h3>
          <p className="text-xl mb-8 opacity-90">
            Get a personalized savings analysis based on your current bill and business needs
          </p>
          <button
            onClick={() => scrollToSection('contact')}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Get Exact Savings Quote
          </button>
        </motion.div>

        {/* How We Get Better Rates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            How We Negotiate Better Rates
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {negotiationFactors.map((factor, index) => (
              <div key={index} className="flex items-start gap-3">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">{factor}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;